import React from 'react'

export interface AvatarProps extends React.HTMLAttributes<HTMLDivElement> {
  /**
   * Avatar size
   */
  size?: 'xs' | 'sm' | 'md' | 'lg' | 'xl' | '2xl'
  /**
   * Avatar shape
   */
  shape?: 'circle' | 'square' | 'rounded'
  /**
   * Image source
   */
  src?: string
  /**
   * Alt text for image
   */
  alt?: string
  /**
   * Fallback text (usually initials)
   */
  fallback?: string
  /**
   * Fallback icon
   */
  fallbackIcon?: React.ReactNode
  /**
   * Online status indicator
   */
  status?: 'online' | 'offline' | 'away' | 'busy'
  /**
   * Status indicator position
   */
  statusPosition?: 'top-right' | 'top-left' | 'bottom-right' | 'bottom-left'
  /**
   * Clickable avatar
   */
  clickable?: boolean
  /**
   * Loading state
   */
  loading?: boolean
  /**
   * Border style
   */
  border?: boolean
  /**
   * Border color
   */
  borderColor?: string
}

const Avatar = React.forwardRef<HTMLDivElement, AvatarProps>(
  (
    {
      size = 'md',
      shape = 'circle',
      src,
      alt,
      fallback,
      fallbackIcon,
      status,
      statusPosition = 'bottom-right',
      clickable = false,
      loading = false,
      border = false,
      borderColor = 'border-gray-200',
      className = '',
      ...props
    },
    ref
  ) => {
    const [imageError, setImageError] = React.useState(false)
    const [imageLoading, setImageLoading] = React.useState(!!src)

    const baseClasses = [
      'relative',
      'inline-flex',
      'items-center',
      'justify-center',
      'font-medium',
      'text-white',
      'bg-gray-500',
      'overflow-hidden',
      'transition-all',
      'duration-200'
    ]

    const sizeClasses = {
      xs: ['w-6', 'h-6', 'text-xs'],
      sm: ['w-8', 'h-8', 'text-sm'],
      md: ['w-10', 'h-10', 'text-base'],
      lg: ['w-12', 'h-12', 'text-lg'],
      xl: ['w-16', 'h-16', 'text-xl'],
      '2xl': ['w-20', 'h-20', 'text-2xl']
    }

    const shapeClasses = {
      circle: ['rounded-full'],
      square: ['rounded-none'],
      rounded: ['rounded-lg']
    }

    const interactiveClasses = clickable ? [
      'cursor-pointer',
      'hover:scale-105',
      'active:scale-95'
    ] : []

    const borderClasses = border ? [
      'border-2',
      borderColor
    ] : []

    const loadingClasses = loading ? ['animate-pulse'] : []

    const avatarClasses = [
      ...baseClasses,
      ...sizeClasses[size],
      ...shapeClasses[shape],
      ...interactiveClasses,
      ...borderClasses,
      ...loadingClasses,
      className
    ].join(' ')

    const statusSizeClasses = {
      xs: 'w-1.5 h-1.5',
      sm: 'w-2 h-2',
      md: 'w-2.5 h-2.5',
      lg: 'w-3 h-3',
      xl: 'w-4 h-4',
      '2xl': 'w-5 h-5'
    }

    const statusColorClasses = {
      online: 'bg-green-500',
      offline: 'bg-gray-400',
      away: 'bg-yellow-500',
      busy: 'bg-red-500'
    }

    const statusPositionClasses = {
      'top-right': 'top-0 right-0',
      'top-left': 'top-0 left-0',
      'bottom-right': 'bottom-0 right-0',
      'bottom-left': 'bottom-0 left-0'
    }

    const handleImageLoad = () => {
      setImageLoading(false)
      setImageError(false)
    }

    const handleImageError = () => {
      setImageLoading(false)
      setImageError(true)
    }

    const getInitials = (name: string) => {
      return name
        .split(' ')
        .map(word => word.charAt(0))
        .join('')
        .toUpperCase()
        .slice(0, 2)
    }

    const getBackgroundColor = (text: string) => {
      const colors = [
        'bg-red-500',
        'bg-blue-500',
        'bg-green-500',
        'bg-yellow-500',
        'bg-purple-500',
        'bg-pink-500',
        'bg-indigo-500',
        'bg-teal-500'
      ]
      const hash = text.split('').reduce((a, b) => {
        a = ((a << 5) - a) + b.charCodeAt(0)
        return a & a
      }, 0)
      return colors[Math.abs(hash) % colors.length]
    }

    return (
      <div
        ref={ref}
        className={avatarClasses}
        {...props}
      >
        {src && !imageError && !imageLoading && (
          <img
            src={src}
            alt={alt}
            className="w-full h-full object-cover"
            onLoad={handleImageLoad}
            onError={handleImageError}
          />
        )}
        
        {imageLoading && (
          <div className="w-full h-full bg-gray-200 animate-pulse" />
        )}
        
        {(imageError || !src) && !imageLoading && (
          <div className={`w-full h-full flex items-center justify-center ${getBackgroundColor(fallback || 'User')}`}>
            {fallbackIcon ? (
              fallbackIcon
            ) : (
              <span className="font-semibold">
                {fallback ? getInitials(fallback) : '?'}
              </span>
            )}
          </div>
        )}
        
        {status && (
          <div
            className={[
              'absolute',
              'rounded-full',
              'border-2',
              'border-white',
              statusSizeClasses[size],
              statusColorClasses[status],
              statusPositionClasses[statusPosition]
            ].join(' ')}
          />
        )}
      </div>
    )
  }
)

Avatar.displayName = 'Avatar'

export { Avatar }
